import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight, CalendarCheck, Lock, Play, FileText, ExternalLink,
  TrendingUp, ShieldCheck, Layers,
} from "lucide-react";
import { FaPodcast, FaSpotify } from "react-icons/fa6";
import { SEO } from "@/components/site/SEO";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { BookFeature } from "@/components/site/BookFeature";
import { VideoModal } from "@/components/site/VideoModal";
import { LeadForm } from "@/components/site/LeadForm";
import { JsonLd, breadcrumbSchema, webPageSchema } from "@/components/site/Schema";
import {
  APPLE_PODCAST_URL, SPOTIFY_URL, VIDEO_GROUPS,
  LOW_STRESS_URL, LOW_STRESS_PLAYLIST,
} from "@/lib/site";
import legacyCover from "@/assets/res16-legacy.jpg";
import budgetMobile from "@/assets/res16-budget.jpg";
import saveSpendCover from "@/assets/res16-save-spend.png";
import sevenStepsCover from "@/assets/res16-seven-steps.jpg";
import guideCover from "@/assets/res16-guide.jpg";
import eyPreview from "@/assets/res-ey-study.png";
import podcastArt from "@/assets/podcast-cashflow.jpg";

const EY_STUDY_URL = "https://customer-assets.emergentagent.com/job_portrait-3/artifacts/7zn0mmtv_EY%20study%3A%20benefits%20of%20integrating%20insurance%20products%20into%20a%20retirement%20plan%204.pdf";

const Section = ({ id, className = "bg-ivory", children }) => (
  <section id={id} className={`${className} py-20 md:py-28`}>
    <div className="container-lr">{children}</div>
  </section>
);

const Eyebrow = ({ children, light }) => (
  <div className="flex items-center gap-4">
    <span className="gold-rule" />
    <p className={`eyebrow ${light ? "text-gold" : ""}`}>{children}</p>
  </div>
);

const GatedForm = ({ list, note }) => (
  <LeadForm list={list} source="resources" note={note} />
);

const VideoCard = ({ v, onPlay }) => (
  <button
    type="button"
    onClick={() => onPlay(v)}
    data-testid={`video-card-${v.id}`}
    className="group text-left lr-card overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
  >
    <div className="relative aspect-video bg-black overflow-hidden">
      <img
        src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
        alt={v.title}
        loading="lazy"
        className="h-full w-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
      />
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gold text-legacy group-hover:scale-105 transition-transform">
          <Play size={22} className="ml-0.5" fill="currentColor" />
        </span>
      </span>
    </div>
    <p className="p-5 font-serif text-lg text-legacy leading-snug">{v.title}</p>
  </button>
);

// Reusable gated resource card — equal height, aligned content, consistent form position.
const ResourceCard = ({ id, testid, img, alt, badge, title, copy, list, note, className = "" }) => (
  <div id={id} data-testid={testid} className={`group scroll-mt-28 h-full flex flex-col overflow-hidden rounded-card border border-taupe/50 bg-white outline-none transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-[0_22px_48px_-30px_rgba(17,17,17,0.5)] focus-within:-translate-y-1 focus-within:border-gold focus-within:shadow-[0_22px_48px_-30px_rgba(17,17,17,0.5)] ${className}`}>
    <img src={img} alt={alt} loading="lazy" className="aspect-video w-full object-cover" />
    <div className="flex flex-1 flex-col p-7">
      <span className="inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1 text-[0.66rem] font-semibold tracking-[0.12em] uppercase bg-taupe/25 text-bronze"><Lock size={11} />{badge}</span>
      <h3 className="mt-4 font-serif text-xl text-legacy leading-tight min-h-[3.25rem]">{title}</h3>
      <p className="mt-2.5 text-slate text-[0.9rem] leading-relaxed min-h-[4.25rem]">{copy}</p>
      <div className="mt-4"><GatedForm list={list} note={note} /></div>
    </div>
  </div>
);

const eyThemes = [
  { icon: TrendingUp, t: "Retirement Income" },
  { icon: ShieldCheck, t: "Protection and Legacy" },
  { icon: Layers, t: "Coordinated Planning" },
];

export default function Resources() {
  const [video, setVideo] = useState(null);
  const location = useLocation();

  // Scroll to the targeted resource card when arriving with a hash (e.g. from
  // the homepage resource buttons: /resources#budget-template).
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let tries = 0;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (tries < 10) {
        tries += 1;
        setTimeout(attempt, 120);
      }
    };
    const t = setTimeout(attempt, 120);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-ivory" data-testid="resources-page">
      <SEO
        title="Financial Education Resources | Legacy Life & Retirement"
        description="Explore free Infinite Banking guides, cash-flow tools, videos, retirement research, the Cashflow Chronicles podcast, and From Regular to Rich."
      />
      <JsonLd id="ld-res-breadcrumb" data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Resources", path: "/resources" }])} />
      <JsonLd id="ld-res-webpage" data={webPageSchema({ path: "/resources", name: "Resources" })} />
      <Header />
      <main>
        {/* Hero */}
        <section data-testid="hero-section" className="relative overflow-hidden bg-[#E9DFCF] pt-[120px] md:pt-[150px] pb-20 md:pb-24 border-b border-bronze/20">
          {/* Linen / paper texture */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-50 md:opacity-70 bg-[repeating-linear-gradient(0deg,rgba(120,100,70,0.05)_0px,rgba(120,100,70,0.05)_1px,transparent_1px,transparent_4px),repeating-linear-gradient(90deg,rgba(120,100,70,0.04)_0px,rgba(120,100,70,0.04)_1px,transparent_1px,transparent_4px)]" />
          {/* Faint grid overlay */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid-lines opacity-[0.16]" />
          {/* Soft Heritage Gold radial glow, upper-right */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-[-4rem] h-[34rem] w-[34rem] rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.28),transparent_65%)]" />
          {/* Faint bronze edge vignette */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 shadow-[inset_0_0_150px_rgba(120,90,50,0.16)]" />
          {/* Subtle bottom divider */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
          <div className="container-lr relative z-10 max-w-3xl">
            <Eyebrow>Learn Before You Decide</Eyebrow>
            <h1 className="mt-6 font-serif font-semibold tracking-display text-legacy text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Start With <span className="accent-word">Education.</span> Build With Intention.
            </h1>
            <p className="mt-7 text-slate text-base md:text-lg leading-relaxed">
              Explore practical resources designed to help you understand Infinite Banking, improve cash flow, prepare for retirement, and build a stronger family legacy.
            </p>
            <p className="mt-4 text-slate/90 text-sm md:text-base leading-relaxed">
              You do not need to make a financial decision today. Start by learning how the different parts of your financial life can work together.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href="#newsletter" data-testid="join-newsletter-cta" className="btn-base btn-primary">
                Join the Newsletter
                <ArrowRight size={18} />
              </a>
              <Link to="/strategy-call" data-testid="hero-strategy-cta" className="btn-base btn-outline-dark">
                <CalendarCheck size={18} />
                Book Your Free Strategy Call
              </Link>
            </div>
          </div>
        </section>

        {/* Free resource library */}
        <Section id="library">
          <div className="max-w-2xl"><Eyebrow>Free Resource Library</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.06]">Guides and tools to learn at your own pace.</h2>
            <p className="mt-5 text-slate text-base leading-relaxed">Each resource is free. Enter your email to receive it and start learning at your own pace — no obligation.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 items-stretch">
            {/* Top row — three equal cards */}
            <ResourceCard
              className="lg:col-span-2" id="seven-steps" testid="resource-7-steps"
              img={sevenStepsCover} alt="7 Simple Steps to Becoming Your Own Banker" badge="Free • Email required"
              title="7 Simple Steps to Becoming Your Own Banker"
              copy="A practical starting point for learning the fundamentals, completing your due diligence, and deciding whether Infinite Banking deserves further consideration."
              list="7 Simple Steps to Becoming Your Own Banker"
            />
            <ResourceCard
              className="lg:col-span-2" id="infinite-banking-guide" testid="resource-infinite-banking-guide"
              img={guideCover} alt="The Infinite Banking Guide" badge="Free • Email required"
              title="The Infinite Banking Guide"
              copy="A clear, practical introduction to the Infinite Banking Concept — how to build safe, accessible capital and become your own source of financing."
              list="The Infinite Banking Guide"
            />
            <ResourceCard
              className="lg:col-span-2" id="save-spend-overview" testid="resource-save-spend"
              img={saveSpendCover} alt="Save & Spend System Overview" badge="Free video • Email required"
              title="Save & Spend System Overview"
              copy="Watch the full Infinite Banking overview and learn how the Save & Spend System works from beginning to end."
              list="Save & Spend System Overview" note="Gated video. Access appears after signup."
            />
            {/* Second row — two equal cards centered below */}
            <ResourceCard
              className="lg:col-span-2 lg:col-start-2" id="budget-template" testid="resource-budget"
              img={budgetMobile} alt="Budget Template + Training" badge="Free • Email required"
              title="Budget Template + Training"
              copy="Create a clearer picture of your income, expenses, savings, debt, and available monthly cash flow."
              list="Budget Template + Training"
            />
            <ResourceCard
              className="lg:col-span-2 lg:col-start-4" id="legacy-builder" testid="resource-legacy-builder"
              img={legacyCover} alt="Legacy Builder Outline" badge="Free • Email required"
              title="Legacy Builder Outline"
              copy="A practical framework for protecting family assets, passing down values, and creating generational momentum."
              list="Legacy Builder Outline"
            />
          </div>
        </Section>

        {/* Featured EY Research */}
        <Section id="ey-study" className="bg-[#EFE8D9]">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 h-20 w-20 border-t border-l border-gold/70" />
              <div className="absolute -bottom-4 -right-4 h-20 w-20 border-b border-r border-gold/70" />
              <img src={eyPreview} alt="EY study cover preview — Benefits of integrating insurance products into a retirement plan" loading="lazy" className="relative rounded-card w-full object-contain bg-white p-4 shadow-[0_30px_60px_-30px_rgba(17,17,17,0.45)]" />
            </div>
            <div>
              <Eyebrow>Featured Research</Eyebrow>
              <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.1]">Why <span className="accent-word">Coordinated Retirement Planning</span> Deserves a Closer Look.</h2>
              <p className="mt-4 text-[0.78rem] font-semibold tracking-wide text-bronze uppercase">Source: EY (Ernst &amp; Young)</p>
              <p className="mt-5 text-slate text-base leading-relaxed">In this study, Ernst &amp; Young analyzed retirement strategies that combined investments with permanent life insurance and a deferred income annuity. The research compared multiple approaches and starting ages to evaluate how coordination may affect retirement income and legacy outcomes.</p>
              <p className="mt-4 text-slate text-base leading-relaxed">The value of the research is not that one product or allocation is appropriate for everyone. It demonstrates why retirement income, protection, liquidity, and legacy should be evaluated together rather than as disconnected decisions.</p>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {eyThemes.map(({ icon: Icon, t }) => (
                  <div key={t} className="flex items-center gap-3 rounded-2xl border border-taupe/50 bg-white p-4">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-legacy text-gold"><Icon size={17} strokeWidth={1.6} /></span>
                    <span className="text-legacy/90 text-[0.85rem] font-medium leading-tight">{t}</span>
                  </div>
                ))}
              </div>
              <a href={EY_STUDY_URL} target="_blank" rel="noopener noreferrer" data-testid="ey-link" className="mt-8 inline-flex items-center gap-2 btn-base btn-primary"><FileText size={17} />Review the EY Study</a>
              <div className="mt-6 border-l-2 border-gold pl-5 text-slate text-[0.82rem] leading-relaxed">
                The study uses modeled assumptions and is provided for general education. Its inclusion does not mean EY endorses Shawn King, Legacy Life &amp; Retirement, or any individualized financial recommendation.
              </div>
            </div>
          </div>
        </Section>

        {/* Video education */}
        <Section id="videos" className="bg-legacy text-ivory">
          <div className="max-w-2xl"><Eyebrow light>Video Education</Eyebrow>
            <h2 className="mt-6 font-serif font-semibold tracking-display text-ivory text-3xl md:text-4xl leading-[1.1]">Watch and learn, topic by topic.</h2>
          </div>
          <div className="mt-12 space-y-14">
            {VIDEO_GROUPS.map((g) => (
              <div key={g.group}>
                <h3 className="font-serif text-2xl text-gold mb-6">{g.group}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {g.videos.map((v) => <VideoCard key={v.id} v={v} onPlay={setVideo} />)}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Cashflow Chronicles */}
        <Section id="podcast" className="bg-ivory">
          <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-center">
            <img src={podcastArt} alt="The Cashflow Chronicles with Shawn King" loading="lazy" className="w-full max-w-sm rounded-card shadow-2xl" />
            <div>
              <Eyebrow>The Cashflow Chronicles</Eyebrow>
              <h2 className="mt-6 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.1]">Conversations about cash flow and building wealth differently.</h2>
              <p className="mt-5 text-slate text-base leading-relaxed">Practical conversations about Infinite Banking, passive income, entrepreneurship, real estate, retirement planning, financial literacy, and building a meaningful legacy.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href={APPLE_PODCAST_URL} target="_blank" rel="noopener noreferrer" className="btn-base btn-primary"><FaPodcast size={18} />Apple Podcasts</a>
                <a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" className="btn-base btn-outline-dark"><FaSpotify size={18} />Spotify</a>
              </div>
            </div>
          </div>
        </Section>

        {/* From Regular to Rich — shared homepage book section */}
        <BookFeature />

        {/* Optional Low-Stress Options resource (matches Financial Solutions) */}
        <section id="low-stress" className="relative overflow-hidden bg-legacy py-24 md:py-28">
          <div className="pointer-events-none absolute inset-0 grid-lines opacity-[0.05]" />
          <div className="pointer-events-none absolute top-[-6rem] left-1/2 -translate-x-1/2 h-96 w-[36rem] rounded-full bg-[radial-gradient(circle,rgba(199,161,90,0.16),transparent_70%)]" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(17,17,17,0.2),rgba(17,17,17,0.75))]" />
          <div className="container-lr relative">
            <div className="mx-auto max-w-3xl rounded-card border border-gold/25 bg-white/[0.04] p-8 md:p-10">
              <p className="eyebrow text-gold">Optional Affiliate Resource</p>
              <h2 className="mt-3 font-serif font-semibold tracking-display text-ivory text-2xl md:text-3xl leading-[1.1]">Learn more about <span className="accent-word">low-stress options.</span></h2>
              <p className="mt-4 text-taupe text-[0.95rem] leading-relaxed">An external educational resource and video playlist for those interested in a low-stress approach to options. This is supplemental — not a core financial pillar.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <a href={LOW_STRESS_URL} target="_blank" rel="noopener noreferrer" data-testid="low-stress-link" className="btn-base btn-gold"><ExternalLink size={17} />Visit Low-Stress Options</a>
                <a href={LOW_STRESS_PLAYLIST} target="_blank" rel="noopener noreferrer" className="btn-base btn-outline-light"><Play size={16} />Watch the Playlist</a>
              </div>
              <div className="mt-6 border-l-2 border-gold/60 pl-5 text-taupe text-[0.84rem] leading-relaxed">
                <p><strong className="text-ivory">Disclosure:</strong> The link is compensated. Options trading involves risk. This is educational. Options are not part of Infinite Banking. This is not individualized investment advice.</p>
                <p className="mt-3"><Link to="/affiliate-disclosure" className="text-gold underline hover:text-ivory font-semibold">Read our full Affiliate Disclosure</Link></p>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter + Final CTA — combined two-box (matches Financial Solutions) */}
        <section id="newsletter" data-testid="newsletter-cta-section" className="bg-[#EFE8D9] py-20 md:py-28 scroll-mt-28">
          <div className="container-lr">
            <div className="grid lg:grid-cols-2 gap-6 items-stretch">
              {/* Newsletter */}
              <div className="rounded-card border border-taupe/50 bg-white p-8 md:p-9">
                <p className="eyebrow text-bronze">Our Weekly Newsletter</p>
                <h3 className="mt-3 font-serif font-semibold text-legacy text-3xl md:text-4xl leading-[1.05]">Mastering Money <span className="accent-word">Mondays</span></h3>
                <p className="mt-2 font-serif italic text-bronze">Build a better financial system. One Monday at a time.</p>
                <LeadForm list="Weekly newsletter sign ups" source="resources" layout="inline" submitLabel="Count Me In" showDisclosure={false} note="A weekly newsletter — not a gated download. Unsubscribe anytime." />
              </div>
              {/* Start Here CTA */}
              <div className="relative overflow-hidden rounded-card bg-legacy p-8 md:p-10 flex flex-col justify-center text-center">
                <div className="pointer-events-none absolute inset-0 grid-lines opacity-[0.06]" />
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(199,161,90,0.2),transparent_60%)]" />
                <div className="relative">
                  <p className="eyebrow text-gold">Ready When You Are</p>
                  <h3 className="mt-3 font-serif font-semibold tracking-display text-ivory text-3xl md:text-[2.4rem] leading-[1.08]">Your financial life should work as one <span className="accent-word">coordinated system.</span></h3>
                  <p className="mt-4 text-taupe text-[0.95rem] leading-relaxed">Start with a straightforward, education-first conversation about your goals, cash flow, and priorities — then decide which tools deserve a place in the larger system.</p>
                  <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/strategy-call" data-testid="resources-final-cta" className="btn-base btn-gold"><CalendarCheck size={18} />Book Your Free Strategy Call</Link>
                    <Link to="/infinite-banking" className="btn-base btn-outline-light">Explore Infinite Banking <ArrowRight size={18} /></Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <VideoModal open={!!video} onClose={() => setVideo(null)} videoId={video?.id} title={video?.title || ""} />
    </div>
  );
}
